import { Button, Flex } from "antd"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useSelector } from "react-redux"
import { toaster } from "components/common/Toast"
import { getActionTranslations } from "utils/translations"
import { NewConfirmModal } from "ui/NewConfirmModal"
import ConnectStripeModal from "layoutsV2/pro/ProNewListingLayout/ConnectStripeModal"
import { STRIPE_TAB } from "pagesV2/pro/Settings/ProSettings/PaymentProviders/constants"
import { spacings } from "themes/variables"
import { routes } from "router"
import styles from "layoutsV2/pro/ProNewListingLayout/ListingActionButtons.module.css"
import { useExperienceWizard } from "./useExperienceWizard"

export const WizardActionButtons = () => {
  const { t } = useTranslation()
  const translations = getActionTranslations(t)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [publishModalVisible, setPublishModalVisible] = useState(false)
  const [stripeModalOpen, setStripeModalOpen] = useState(false)

  const user = useSelector((store) => store.user)
  const tripData = useSelector((state) => state?.trips)
  const stripeData = user?.guide?.stripe_account_data || {}
  const payoutsEnabled = !!stripeData.payouts_enabled
  const chargesEnabled = !!stripeData.charges_enabled
  const noStripeError = !stripeData.error_message
  const shouldShowStripeWarning = !payoutsEnabled || !chargesEnabled || !noStripeError

  const routeType = searchParams.get("route") // "published" | "deactivated" | null
  const isEditMode = Boolean(routeType)

  const { isFirstStep, isLastStep, isSaving, handleNext, handlePrevious, handleSaveDraft, handlePublish } =
    useExperienceWizard()

  const getBackRoute = () => {
    if (routeType === "deactivated") return routes.proExperiencePathDeactivatedPath()
    if (routeType === "published") return routes.proExperiencePathPublishedPath()
    return routes.proExperiencePathDraft()
  }

  const onSaveAndExit = () => {
    handleSaveDraft(() => {
      navigate(getBackRoute())
    })
  }

  const onPublish = () => {
    handlePublish(() => {
      toaster.success(t("experiences.successful_publish"))
      setPublishModalVisible(false)
      setTimeout(() => navigate(routes.proExperiencesPath()), 300)
    })
  }

  const onPublishClick = () => {
    if (shouldShowStripeWarning) {
      setStripeModalOpen(true)
    } else {
      setPublishModalVisible(true)
    }
  }

  const onStripeConnect = () => {
    handleSaveDraft(() => {
      toaster.success(t("experiences.stripe_modal.draft_saved"))
      setStripeModalOpen(false)
      navigate(routes.proSettingsPaymentProviders({}, { tab: STRIPE_TAB }))
    })
  }

  return (
    <div className={styles.wrapper} style={{ flexWrap: "wrap" }}>
      <Flex justify="space-between" gap={10} className={styles.actionWrap}>
        {!isFirstStep && (
          <Button
            className="pr-xl pl-xl previewBtn"
            color="primary"
            variant="outlined"
            size="large"
            onClick={handlePrevious}
            disabled={isSaving}
          >
            {t("global.previous")}
          </Button>
        )}
        {isEditMode && (
          <Button
            className="pr-xl pl-xl previewBtn"
            color="primary"
            variant="outlined"
            size="large"
            onClick={() => navigate(getBackRoute())}
            disabled={isSaving}
          >
            {t("global.cancel")}
          </Button>
        )}
      </Flex>

      <Flex gap={spacings.S} className="save-wrapper" justify="flex-end">
        <Button
          className="pr-xl pl-xl saveExitBtn"
          color="primary"
          variant={isEditMode ? "solid" : "outlined"}
          size="large"
          onClick={onSaveAndExit}
          disabled={isSaving}
        >
          {isEditMode ? t("listings.save_updates") : t("listings.save_draft_and_exit")}
        </Button>

        {isLastStep ? (
          <Button
            className="pr-xl pl-xl"
            color="primary"
            variant="solid"
            size="large"
            onClick={onPublishClick}
            disabled={isSaving}
          >
            {t("listings.save_publish")}
          </Button>
        ) : (
          <Button
            className="pr-xl pl-xl nextBtn"
            color="primary"
            variant="solid"
            size="large"
            onClick={handleNext}
            disabled={isSaving}
          >
            {t("global.next")}
          </Button>
        )}
      </Flex>

      <NewConfirmModal
        onClose={() => setPublishModalVisible(false)}
        title={translations?.publish?.title}
        message={translations?.publish?.message}
        highlightText={tripData?.trip?.title}
        visible={publishModalVisible}
        onConfirm={onPublish}
        confirmLabel="Publish"
      />

      <ConnectStripeModal
        open={stripeModalOpen}
        onCancel={() => setStripeModalOpen(false)}
        onConnect={onStripeConnect}
      />
    </div>
  )
}
